import { useMemo } from 'react';
import { FormData } from '../../Types/forms';
import { FormContextProvider } from './FormContext';
import FormField from './FormField';
import FormFieldGroup from './FormFieldGroup';
import FormWizard from './FormWizard';
import './FormRenderer.scss';

type FormRendererProps = {
	form: FormData;
	disabled?: boolean;
	children?: React.ReactNode;
};

const FormRenderer = ({ form, disabled, children }: FormRendererProps) => {
	const hasWizard = form.wizardSteps.length > 0;
	const sortedGroups = useMemo(() => [...form.fieldGroups].sort((a, b) => a.order - b.order), [form.fieldGroups]);
	const groupKeys = useMemo(() => new Set(sortedGroups.map((g) => g.key)), [sortedGroups]);

	// Fields not assigned to any group (and not in a wizard step handled by FormWizard)
	const ungroupedFields = useMemo(
		() => form.fields.filter((f) => (!f.groupKey || !groupKeys.has(f.groupKey)) && !f.wizardStepKey),
		[form.fields, groupKeys]
	);

	return (
		<FormContextProvider form={form} disabled={disabled}>
			<div className='biz-form-renderer'>
				{hasWizard ? (
					<FormWizard steps={form.wizardSteps} fields={form.fields} groups={form.fieldGroups} />
				) : (
					<>
						<div className='biz-form-renderer__fields'>
							{ungroupedFields.map((field) => (
								<FormField key={field.key} field={field} />
							))}
						</div>

						{sortedGroups.map((group) => {
							const groupFields = form.fields.filter((f) => f.groupKey === group.key);
							return <FormFieldGroup key={group.key} group={group} fields={groupFields} />;
						})}
					</>
				)}
			</div>
			{children}
		</FormContextProvider>
	);
};

export default FormRenderer;
